import { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertTriangle, Wallet, Plus, Camera } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient, extractErrorMessage, getStoredToken } from '@/lib/api';

const API_BASE = (import.meta.env.VITE_API_URL as string) || 'https://api.jasrhub.com';

interface Ledger { id: string; occurred_at: string; entry_type: string; amount_sar: string; description: string }
interface Driver { id: string; full_name_en: string | null; balance: string }

const fmtSar = (n: number) => 'SAR ' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function MyAccountPage() {
  const [driverId, setDriverId] = useState<string | null>(null);
  const [driver, setDriver] = useState<Driver | null>(null);
  const [ledger, setLedger] = useState<Ledger[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSubmit, setShowSubmit] = useState(false);
  const [category, setCategory] = useState('fuel');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [receipt, setReceipt] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const refresh = useCallback(async () => {
    try {
      const me = await apiClient.get<{ driver: { id: string } }>('/api/v1/driver-portal/me/dashboard');
      const id = me.data.driver?.id;
      if (!id) throw new Error('Not linked to a driver record. Ask admin to link your phone to a driver.');
      setDriverId(id);
      const r = await apiClient.get<{ driver: Driver; ledger: Ledger[] }>('/api/v1/driver-finance/drivers/' + id);
      setDriver(r.data.driver);
      setLedger(r.data.ledger);
      setError(null);
    } catch (e) { setError(extractErrorMessage(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  async function submitReimbursable() {
    if (!driverId) return;
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) return;
    setBusy(true);
    try {
      let receiptUrl: string | undefined;
      if (receipt) {
        const fd = new FormData();
        fd.append('file', receipt);
        const jwt = getStoredToken();
        const up = await fetch(API_BASE + '/api/v1/uploads/receipt', {
          method: 'POST',
          headers: jwt ? { Authorization: 'Bearer ' + jwt } : {},
          body: fd,
        });
        if (up.ok) { const j = await up.json(); receiptUrl = j.url ?? undefined; }
      }
      await apiClient.post('/api/v1/driver-finance/reimbursables', {
        driverId, category, amountSar: amt, description: description || undefined, receiptPhotoUrl: receiptUrl,
      });
      setShowSubmit(false); setAmount(''); setDescription(''); setReceipt(null); setCategory('fuel');
      await refresh();
    } catch (e) { window.alert(extractErrorMessage(e)); }
    finally { setBusy(false); }
  }

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (error || !driver) return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-3 p-6">
      <AlertTriangle className="h-8 w-8 text-amber-500" />
      <div className="text-sm text-muted-foreground text-center max-w-sm">{error ?? 'Not found'}</div>
      <Link to="/dashboard"><Button variant="outline">Back</Button></Link>
    </div>
  );

  const bal = Number(driver.balance);

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/dashboard"><Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Dashboard</Button></Link>
          <div className="text-sm font-medium inline-flex items-center gap-2"><Wallet className="h-4 w-4" /> My account</div>
          <div className="w-12" />
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-4 space-y-4">
        <div className="rounded-lg border border-border p-4 text-center">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Your balance</div>
          <div className={'text-3xl font-light mt-1 ' + (bal >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400')}>{fmtSar(bal)}</div>
          <div className="text-xs text-muted-foreground mt-1">{bal >= 0 ? 'Company owes you' : 'You owe company'}</div>
        </div>

        <Button variant="gold" onClick={() => setShowSubmit((s) => !s)} className="w-full gap-2"><Plus className="h-4 w-4" /> Submit expense for reimbursement</Button>

        {showSubmit && (
          <div className="rounded-lg border border-border p-3 space-y-2">
            <select value={category} onChange={(e) => setCategory(e.target.value)}
              className="w-full rounded border border-border bg-background px-3 py-2 text-sm">
              <option value="fuel">Fuel</option>
              <option value="tolls">Tolls</option>
              <option value="repair">Repair</option>
              <option value="parking">Parking</option>
              <option value="meals">Meals</option>
              <option value="other">Other</option>
            </select>
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount SAR"
              className="w-full rounded border border-border bg-background px-3 py-2 text-sm" />
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description"
              className="w-full rounded border border-border bg-background px-3 py-2 text-sm" rows={2} />
            <Button variant="outline" size="sm" className="w-full gap-2" onClick={() => fileRef.current?.click()}>
              <Camera className="h-3.5 w-3.5" /> {receipt ? receipt.name : 'Attach receipt photo'}
            </Button>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden"
              onChange={(e) => setReceipt(e.target.files?.[0] ?? null)} />
            <div className="flex gap-2">
              <Button onClick={submitReimbursable} disabled={busy} variant="gold" size="sm" className="flex-1">Submit</Button>
              <Button onClick={() => setShowSubmit(false)} variant="ghost" size="sm">Cancel</Button>
            </div>
          </div>
        )}

        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Recent activity</div>
          {ledger.length === 0 ? <div className="rounded-lg border border-border p-6 text-center text-sm text-muted-foreground">No entries yet.</div>
          : <div className="space-y-1">
              {ledger.slice(0, 50).map((l) => {
                const a = Number(l.amount_sar);
                return (
                  <div key={l.id} className="rounded-lg border border-border p-2 text-xs flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="truncate">{l.description}</div>
                      <div className="text-muted-foreground">{new Date(l.occurred_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                    </div>
                    <div className={'font-mono ' + (a >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400')}>{a >= 0 ? '+' : ''}{fmtSar(a)}</div>
                  </div>
                );
              })}
            </div>}
        </div>
      </main>
    </div>
  );
}
